import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { forkJoin, of, pipe, switchMap, tap } from 'rxjs';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import {
  AcademyContentState,
  initialAcademyContentState,
} from './entities/academy-content-store.model';
import {
  ChapterCreateRequest,
  TranscriptCueCreateRequest,
} from './entities/academy.model';
import { ContentReadService } from './infra/content-read.service';
import { ContentAuthoringService } from './infra/content-authoring.service';

const ALL_OPS = [
  'loadChapters',
  'loadCaptionLanguages',
  'loadCaption',
  'searchTranscript',
  'createChapter',
  'createChapters',
  'loadAuthoredChapters',
  'loadAuthoredChaptersForEvents',
  'loadAuthoredChaptersByAsset',
  'createTranscriptCue',
  'importTranscriptCues',
  'loadAuthoredCues',
  'loadAuthoredCuesForEvents',
  'loadAuthoredCuesByAsset',
  'commitChapters',
  'commitChaptersByAsset',
  'commitCues',
  'commitCuesByAsset',
] as const;

/** Live-recording target — identified by `eventId`. */
interface EventTarget {
  eventId: string;
}

/** Course-item target — identified by `${courseId}:${itemId}`. */
interface ItemTarget {
  courseId: string;
  itemId: string;
}

type ChaptersInput = EventTarget | ItemTarget;
type CaptionInput = (EventTarget | ItemTarget) & { lang: string };
type TranscriptInput = (EventTarget | ItemTarget) & {
  q: string;
  lang?: string;
};

function isItemTarget(input: EventTarget | ItemTarget): input is ItemTarget {
  return (input as ItemTarget).itemId != null;
}

/**
 * Consumer-side content reads (chapters / captions / transcript) are *gated by the live
 * event's audience*: a viewer outside the audience legitimately gets `403`. That is "no
 * content available to you", not a failure to surface — so a `403` is swallowed (no danger
 * toast; the read resolves empty / errors silently) while every other status still raises
 * the toast. The unavailable case is already communicated by the playback-token error UI.
 */
function isAudienceDenied(error: HttpErrorResponse): boolean {
  return error?.status === 403;
}

/** Cache key: `eventId` for live recordings, `${courseId}:${itemId}` for course items. */
function contentKey(input: EventTarget | ItemTarget): string {
  return isItemTarget(input)
    ? `${input.courseId}:${input.itemId}`
    : input.eventId;
}

/** Append a batch of authored items into their `LiveEventId` buckets. */
function appendByEvent<T extends { LiveEventId: string }>(
  map: Record<string, T[]>,
  items: T[]
): Record<string, T[]> {
  const next = { ...map };
  for (const item of items) {
    next[item.LiveEventId] = [...(next[item.LiveEventId] ?? []), item];
  }
  return next;
}

/**
 * Replace the buckets of a set of events from one flat batch read. Every requested id is
 * seeded empty first, so an event with no authored items resolves to `[]` (a definitive
 * "none") rather than `undefined` (still loading) for the course-content counts.
 */
function distributeByEvent<T extends { LiveEventId: string }>(
  map: Record<string, T[]>,
  eventIds: string[],
  items: T[]
): Record<string, T[]> {
  const next = { ...map };
  for (const id of eventIds) next[id] = [];
  for (const item of items) {
    next[item.LiveEventId] = [...(next[item.LiveEventId] ?? []), item];
  }
  return next;
}

export const AcademyContentStore = signalStore(
  { providedIn: 'root' },
  withState<AcademyContentState>(initialAcademyContentState),
  withReqState(...ALL_OPS),
  withMethods(
    (
      store,
      content = inject(ContentReadService),
      authoring = inject(ContentAuthoringService),
      errorStore = inject(ErrorStore)
    ) => {
      const loadChapters = rxMethod<ChaptersInput>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadChapters'))),
          switchMap((input) => {
            const key = contentKey(input);
            const request$ = isItemTarget(input)
              ? content.itemChapters(input.courseId, input.itemId)
              : content.chapters(input.eventId);
            return request$.pipe(
              tapResponse({
                next: (chapters) =>
                  patchState(
                    store,
                    {
                      chaptersByKey: {
                        ...store.chaptersByKey(),
                        [key]: chapters,
                      },
                    },
                    setPropLoaded('loadChapters')
                  ),
                error: (error: HttpErrorResponse) => {
                  if (isAudienceDenied(error)) {
                    patchState(
                      store,
                      {
                        chaptersByKey: {
                          ...store.chaptersByKey(),
                          [key]: [],
                        },
                      },
                      setPropLoaded('loadChapters')
                    );
                    return;
                  }
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadChapters', error));
                },
              })
            );
          })
        )
      );

      // Only live recordings expose a caption-language list endpoint
      // (`GET /LiveEvents/{id}/captions`); the OP keys results by `eventId`.
      const loadCaptionLanguages = rxMethod<EventTarget>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadCaptionLanguages'))),
          switchMap((input) =>
            content.captionLanguages(input.eventId).pipe(
              tapResponse({
                next: (langs) =>
                  patchState(
                    store,
                    {
                      captionLanguagesByKey: {
                        ...store.captionLanguagesByKey(),
                        [input.eventId]: langs,
                      },
                    },
                    setPropLoaded('loadCaptionLanguages')
                  ),
                error: (error: HttpErrorResponse) => {
                  if (isAudienceDenied(error)) {
                    patchState(
                      store,
                      {
                        captionLanguagesByKey: {
                          ...store.captionLanguagesByKey(),
                          [input.eventId]: [],
                        },
                      },
                      setPropLoaded('loadCaptionLanguages')
                    );
                    return;
                  }
                  errorStore.setHttpError(error);
                  patchState(
                    store,
                    setPropError('loadCaptionLanguages', error)
                  );
                },
              })
            )
          )
        )
      );

      const loadCaption = rxMethod<CaptionInput>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadCaption'))),
          switchMap((input) => {
            const cacheKey = `${contentKey(input)}#${input.lang}`;
            const request$ = isItemTarget(input)
              ? content.itemCaption(input.courseId, input.itemId, input.lang)
              : content.caption(input.eventId, input.lang);
            return request$.pipe(
              tapResponse({
                next: (vtt) =>
                  patchState(
                    store,
                    {
                      captionTextByKey: {
                        ...store.captionTextByKey(),
                        [cacheKey]: vtt,
                      },
                    },
                    setPropLoaded('loadCaption')
                  ),
                error: (error: HttpErrorResponse) => {
                  if (!isAudienceDenied(error)) errorStore.setHttpError(error);
                  patchState(store, setPropError('loadCaption', error));
                },
              })
            );
          })
        )
      );

      const searchTranscript = rxMethod<TranscriptInput>(
        pipe(
          tap(() => patchState(store, setPropLoading('searchTranscript'))),
          switchMap((input) => {
            const cacheKey = `${contentKey(input)}#${input.q}#${
              input.lang ?? ''
            }`;
            const request$ = isItemTarget(input)
              ? content.itemTranscriptSearch(
                  input.courseId,
                  input.itemId,
                  input.q,
                  input.lang
                )
              : content.transcriptSearch(input.eventId, input.q, input.lang);
            return request$.pipe(
              tapResponse({
                next: (matches) =>
                  patchState(
                    store,
                    {
                      transcriptMatchesByKey: {
                        ...store.transcriptMatchesByKey(),
                        [cacheKey]: matches,
                      },
                    },
                    setPropLoaded('searchTranscript')
                  ),
                error: (error: HttpErrorResponse) => {
                  if (isAudienceDenied(error)) {
                    patchState(
                      store,
                      {
                        transcriptMatchesByKey: {
                          ...store.transcriptMatchesByKey(),
                          [cacheKey]: [],
                        },
                      },
                      setPropLoaded('searchTranscript')
                    );
                    return;
                  }
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('searchTranscript', error));
                },
              })
            );
          })
        )
      );

      // ---- Producer authoring (Chapters / TranscriptCues over generic OData) ----

      const createChapter = rxMethod<ChapterCreateRequest>(
        pipe(
          tap(() => patchState(store, setPropLoading('createChapter'))),
          switchMap((vm) =>
            authoring.createChapter(vm).pipe(
              tapResponse({
                next: (created) =>
                  patchState(
                    store,
                    {
                      authoredChaptersByEvent: appendByEvent(
                        store.authoredChaptersByEvent(),
                        [created]
                      ),
                    },
                    setPropLoaded('createChapter')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('createChapter', error));
                },
              })
            )
          )
        )
      );

      // Bulk add — one `rxMethod` call for N drafts. A loop over `createChapter` would
      // self-cancel (switchMap); the service folds the POSTs into a single forkJoin.
      const createChapters = rxMethod<ChapterCreateRequest[]>(
        pipe(
          tap(() => patchState(store, setPropLoading('createChapters'))),
          switchMap((vms) =>
            authoring.createChapters(vms).pipe(
              tapResponse({
                next: (created) =>
                  patchState(
                    store,
                    {
                      authoredChaptersByEvent: appendByEvent(
                        store.authoredChaptersByEvent(),
                        created
                      ),
                    },
                    setPropLoaded('createChapters')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('createChapters', error));
                },
              })
            )
          )
        )
      );

      // Recording authoring reads go through the custom event-scoped endpoint
      // (`GET /LiveEvents/{id}/chapters`), not the generic OData set: `LiveEventId` is not a
      // persisted column on the `Chapter` entity (resolved to `VideoAssetId` at create), so a
      // `$filter=LiveEventId` would 400. The response still carries `ChapterId` for the diff.
      const loadAuthoredChapters = rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadAuthoredChapters'))),
          switchMap((liveEventId) =>
            content.chapters(liveEventId).pipe(
              tapResponse({
                next: (chapters) =>
                  patchState(
                    store,
                    {
                      authoredChaptersByEvent: {
                        ...store.authoredChaptersByEvent(),
                        [liveEventId]: chapters,
                      },
                    },
                    setPropLoaded('loadAuthoredChapters')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(
                    store,
                    setPropError('loadAuthoredChapters', error)
                  );
                },
              })
            )
          )
        )
      );

      /**
       * Diff-aware chapter save: forkJoin the bulk DELETE (removed/changed ids) and bulk POST
       * (new/changed rows), then re-read so `StartSeconds` ordering comes from the server. One
       * emission — never a per-row loop (switchMap would self-cancel). No PUT exists, so an
       * edit arrives here as a delete-id + a create. Empty diff → Loaded, no request.
       */
      const commitChapters = rxMethod<{
        liveEventId: string;
        create: ChapterCreateRequest[];
        deleteIds: string[];
      }>(
        pipe(
          tap(() => patchState(store, setPropLoading('commitChapters'))),
          switchMap(({ liveEventId, create, deleteIds }) => {
            if (create.length === 0 && deleteIds.length === 0) {
              patchState(store, setPropLoaded('commitChapters'));
              return of(null);
            }
            return forkJoin({
              deleted: authoring.deleteChapters(deleteIds),
              created: authoring.createChapters(create),
            }).pipe(
              switchMap(() => content.chapters(liveEventId)),
              tapResponse({
                next: (chapters) =>
                  patchState(
                    store,
                    {
                      authoredChaptersByEvent: {
                        ...store.authoredChaptersByEvent(),
                        [liveEventId]: chapters,
                      },
                    },
                    setPropLoaded('commitChapters')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('commitChapters', error));
                },
              })
            );
          })
        )
      );

      const createTranscriptCue = rxMethod<TranscriptCueCreateRequest>(
        pipe(
          tap(() => patchState(store, setPropLoading('createTranscriptCue'))),
          switchMap((vm) =>
            authoring.createTranscriptCue(vm).pipe(
              tapResponse({
                next: (created) =>
                  patchState(
                    store,
                    {
                      authoredCuesByEvent: appendByEvent(
                        store.authoredCuesByEvent(),
                        [created]
                      ),
                    },
                    setPropLoaded('createTranscriptCue')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('createTranscriptCue', error));
                },
              })
            )
          )
        )
      );

      const importTranscriptCues = rxMethod<TranscriptCueCreateRequest[]>(
        pipe(
          tap(() => patchState(store, setPropLoading('importTranscriptCues'))),
          switchMap((vms) =>
            authoring.importTranscriptCues(vms).pipe(
              tapResponse({
                next: (created) =>
                  patchState(
                    store,
                    {
                      authoredCuesByEvent: appendByEvent(
                        store.authoredCuesByEvent(),
                        created
                      ),
                    },
                    setPropLoaded('importTranscriptCues')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(
                    store,
                    setPropError('importTranscriptCues', error)
                  );
                },
              })
            )
          )
        )
      );

      /**
       * Diff-aware cue save across all languages in ONE emission. `create` carries each cue's
       * `LanguageCode`; `deleteIds` spans every language (incl. a wholesale "replace language").
       * Re-reads the full bucket so other-language counts stay correct. Empty diff → Loaded.
       */
      const commitCues = rxMethod<{
        liveEventId: string;
        create: TranscriptCueCreateRequest[];
        deleteIds: string[];
      }>(
        pipe(
          tap(() => patchState(store, setPropLoading('commitCues'))),
          switchMap(({ liveEventId, create, deleteIds }) => {
            if (create.length === 0 && deleteIds.length === 0) {
              patchState(store, setPropLoaded('commitCues'));
              return of(null);
            }
            return forkJoin({
              deleted: authoring.deleteTranscriptCues(deleteIds),
              created: authoring.importTranscriptCues(create),
            }).pipe(
              switchMap(() => content.transcriptCues(liveEventId)),
              tapResponse({
                next: (cues) =>
                  patchState(
                    store,
                    {
                      authoredCuesByEvent: {
                        ...store.authoredCuesByEvent(),
                        [liveEventId]: cues,
                      },
                    },
                    setPropLoaded('commitCues')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('commitCues', error));
                },
              })
            );
          })
        )
      );

      // Recording cue reads use the custom event-scoped endpoint
      // (`GET /LiveEvents/{id}/transcript`) — same rationale as chapters; the response carries
      // `TranscriptCueId` (for the diff) and every language. `lang`, when given, narrows the
      // bucket client-side (the editor reads all languages, so it is normally omitted).
      const loadAuthoredCues = rxMethod<{ liveEventId: string; lang?: string }>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadAuthoredCues'))),
          switchMap(({ liveEventId, lang }) =>
            content.transcriptCues(liveEventId).pipe(
              tapResponse({
                next: (all) =>
                  patchState(
                    store,
                    {
                      authoredCuesByEvent: {
                        ...store.authoredCuesByEvent(),
                        [liveEventId]: lang
                          ? all.filter((c) => c.LanguageCode === lang)
                          : all,
                      },
                    },
                    setPropLoaded('loadAuthoredCues')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadAuthoredCues', error));
                },
              })
            )
          )
        )
      );

      // Batched count load for the course-content page. The generic OData set can no longer be
      // filtered by `LiveEventId`, so this fans out one custom-endpoint read per recording
      // (`GET /LiveEvents/{id}/chapters`) and regroups the flattened responses by `LiveEventId`
      // (which the response carries). Buckets are shared with the single-event loads, so opening
      // the editor afterwards finds a warm cache. Empty input issues no request.
      const loadAuthoredChaptersForEvents = rxMethod<string[]>(
        pipe(
          tap(() =>
            patchState(store, setPropLoading('loadAuthoredChaptersForEvents'))
          ),
          switchMap((liveEventIds) => {
            if (liveEventIds.length === 0) {
              patchState(store, setPropLoaded('loadAuthoredChaptersForEvents'));
              return of(null);
            }
            return forkJoin(
              liveEventIds.map((id) => content.chapters(id))
            ).pipe(
              tapResponse({
                next: (lists) =>
                  patchState(
                    store,
                    {
                      authoredChaptersByEvent: distributeByEvent(
                        store.authoredChaptersByEvent(),
                        liveEventIds,
                        lists.flat()
                      ),
                    },
                    setPropLoaded('loadAuthoredChaptersForEvents')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(
                    store,
                    setPropError('loadAuthoredChaptersForEvents', error)
                  );
                },
              })
            );
          })
        )
      );

      const loadAuthoredCuesForEvents = rxMethod<string[]>(
        pipe(
          tap(() =>
            patchState(store, setPropLoading('loadAuthoredCuesForEvents'))
          ),
          switchMap((liveEventIds) => {
            if (liveEventIds.length === 0) {
              patchState(store, setPropLoaded('loadAuthoredCuesForEvents'));
              return of(null);
            }
            return forkJoin(
              liveEventIds.map((id) => content.transcriptCues(id))
            ).pipe(
              tapResponse({
                next: (lists) =>
                  patchState(
                    store,
                    {
                      authoredCuesByEvent: distributeByEvent(
                        store.authoredCuesByEvent(),
                        liveEventIds,
                        lists.flat()
                      ),
                    },
                    setPropLoaded('loadAuthoredCuesForEvents')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(
                    store,
                    setPropError('loadAuthoredCuesForEvents', error)
                  );
                },
              })
            );
          })
        )
      );

      // ---- Course-upload items: author chapters/cues by VideoAssetId ----
      // Upload lessons have no LiveEvent, so the recording editor's LiveEventId reads/commits
      // don't reach them. These mirror the event variants but filter + re-read by VideoAssetId
      // (the column the create bodies already write), keying the shared buckets by the asset id
      // — which the editor uses as its target id in course-item mode.
      const loadAuthoredChaptersByAsset = rxMethod<string>(
        pipe(
          tap(() =>
            patchState(store, setPropLoading('loadAuthoredChaptersByAsset'))
          ),
          switchMap((videoAssetId) =>
            authoring
              .listChapters({
                filter: `VideoAssetId eq ${videoAssetId}`,
                orderby: 'StartSeconds',
              })
              .pipe(
                tapResponse({
                  next: (page) =>
                    patchState(
                      store,
                      {
                        authoredChaptersByEvent: {
                          ...store.authoredChaptersByEvent(),
                          [videoAssetId]: page.value,
                        },
                      },
                      setPropLoaded('loadAuthoredChaptersByAsset')
                    ),
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(
                      store,
                      setPropError('loadAuthoredChaptersByAsset', error)
                    );
                  },
                })
              )
          )
        )
      );

      const commitChaptersByAsset = rxMethod<{
        videoAssetId: string;
        create: ChapterCreateRequest[];
        deleteIds: string[];
      }>(
        pipe(
          tap(() => patchState(store, setPropLoading('commitChaptersByAsset'))),
          switchMap(({ videoAssetId, create, deleteIds }) => {
            if (create.length === 0 && deleteIds.length === 0) {
              patchState(store, setPropLoaded('commitChaptersByAsset'));
              return of(null);
            }
            return forkJoin({
              deleted: authoring.deleteChapters(deleteIds),
              created: authoring.createChapters(create),
            }).pipe(
              switchMap(() =>
                authoring.listChapters({
                  filter: `VideoAssetId eq ${videoAssetId}`,
                  orderby: 'StartSeconds',
                })
              ),
              tapResponse({
                next: (page) =>
                  patchState(
                    store,
                    {
                      authoredChaptersByEvent: {
                        ...store.authoredChaptersByEvent(),
                        [videoAssetId]: page.value,
                      },
                    },
                    setPropLoaded('commitChaptersByAsset')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(
                    store,
                    setPropError('commitChaptersByAsset', error)
                  );
                },
              })
            );
          })
        )
      );

      const loadAuthoredCuesByAsset = rxMethod<string>(
        pipe(
          tap(() =>
            patchState(store, setPropLoading('loadAuthoredCuesByAsset'))
          ),
          switchMap((videoAssetId) =>
            authoring
              .listTranscriptCues({
                filter: `VideoAssetId eq ${videoAssetId}`,
                orderby: 'StartSeconds',
              })
              .pipe(
                tapResponse({
                  next: (page) =>
                    patchState(
                      store,
                      {
                        authoredCuesByEvent: {
                          ...store.authoredCuesByEvent(),
                          [videoAssetId]: page.value,
                        },
                      },
                      setPropLoaded('loadAuthoredCuesByAsset')
                    ),
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(
                      store,
                      setPropError('loadAuthoredCuesByAsset', error)
                    );
                  },
                })
              )
          )
        )
      );

      const commitCuesByAsset = rxMethod<{
        videoAssetId: string;
        create: TranscriptCueCreateRequest[];
        deleteIds: string[];
      }>(
        pipe(
          tap(() => patchState(store, setPropLoading('commitCuesByAsset'))),
          switchMap(({ videoAssetId, create, deleteIds }) => {
            if (create.length === 0 && deleteIds.length === 0) {
              patchState(store, setPropLoaded('commitCuesByAsset'));
              return of(null);
            }
            return forkJoin({
              deleted: authoring.deleteTranscriptCues(deleteIds),
              created: authoring.importTranscriptCues(create),
            }).pipe(
              switchMap(() =>
                authoring.listTranscriptCues({
                  filter: `VideoAssetId eq ${videoAssetId}`,
                  orderby: 'StartSeconds',
                })
              ),
              tapResponse({
                next: (page) =>
                  patchState(
                    store,
                    {
                      authoredCuesByEvent: {
                        ...store.authoredCuesByEvent(),
                        [videoAssetId]: page.value,
                      },
                    },
                    setPropLoaded('commitCuesByAsset')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('commitCuesByAsset', error));
                },
              })
            );
          })
        )
      );

      const reset = (): void =>
        patchState(store, initialAcademyContentState, setPropInit(...ALL_OPS));

      return {
        loadChapters,
        loadCaptionLanguages,
        loadCaption,
        searchTranscript,
        createChapter,
        createChapters,
        loadAuthoredChapters,
        loadAuthoredChaptersForEvents,
        loadAuthoredChaptersByAsset,
        commitChapters,
        commitChaptersByAsset,
        createTranscriptCue,
        importTranscriptCues,
        commitCues,
        commitCuesByAsset,
        loadAuthoredCues,
        loadAuthoredCuesForEvents,
        loadAuthoredCuesByAsset,
        reset,
      };
    }
  )
);
